import type { HTMLAttributes, ReactNode } from "react";

import { cn } from "@/lib/cn";

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "min-w-0 rounded-card border border-border bg-surface shadow-card",
        className,
      )}
      {...props}
    />
  );
}

export function CardHeader({
  title,
  subtitle,
  action,
  className,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        // the action drops under the title when the card is too narrow for both
        "flex flex-wrap items-start justify-between gap-x-3 gap-y-2 px-4 pt-4 pb-2",
        className,
      )}
    >
      <div className="min-w-0 flex-1 basis-56">
        <h3 className="text-sm font-semibold text-fg">{title}</h3>
        {subtitle ? (
          <p className="mt-0.5 text-xs text-muted">{subtitle}</p>
        ) : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

export function CardBody({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  // Under a CardHeader the header supplies the top gap; as the card's first
  // child the body needs its own, or the content sits on the top border.
  return <div className={cn("px-4 pb-4 first:pt-4", className)} {...props} />;
}

/** A labelled figure: "Above 200-DMA / 37.5%". */
export function Stat({
  label,
  value,
  sub,
  className,
  valueClassName,
}: {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  className?: string;
  valueClassName?: string;
}) {
  return (
    <div className={cn("min-w-0", className)}>
      <div className="text-2xs font-medium tracking-wide text-subtle uppercase">
        {label}
      </div>
      <div
        className={cn(
          "num mt-0.5 text-xl font-semibold text-fg",
          valueClassName,
        )}
      >
        {value}
      </div>
      {sub ? <div className="num mt-0.5 text-xs text-muted">{sub}</div> : null}
    </div>
  );
}
